import {
  createContext,
  ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import fetchData from '@/utils/fetchData';
import useError from '@/hooks/useError';

type Props = {
  children: ReactNode;
};

export type Bank = {
  clientIdentificationNumber: string;
  clientPersonalId: string;
  bankName: string;
  registrationNumber: string;
  accountNumber: string;
  bic: string;
  homePageUrl: string;
  branchPageUrl: string;
  receiver: string;
  masterAccount: boolean;
  shortName: string;
};

export type Banks = {
  templates: Array<Bank>;
};

export type BanksContextType = {
  banks?: Banks;
  fetchBanks: () => Promise<Banks | void>;
};

export const BanksContext = createContext<BanksContextType>(
  {} as BanksContextType,
);

export function BanksProvider({ children }: Props): React.JSX.Element {
  const [banks, setBanks] = useState<Banks>();
  const { showError } = useError();

  const fetchBanks = useCallback(async (): Promise<Banks | void> => {
    try {
      const fetchedBanks = await fetchData('/client/banks');

      setBanks(fetchedBanks);

      return fetchedBanks;
    } catch (e) {
      showError();
      throw e;
    }
  }, [showError]);

  const banksContextValue = useMemo(
    () => ({
      banks,
      fetchBanks,
    }),
    [banks, fetchBanks],
  );

  return (
    <BanksContext.Provider value={banksContextValue}>
      {children}
    </BanksContext.Provider>
  );
}
